import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import LeadColumn from './LeadColumn';
import LeadCard from './LeadCard';
import LostReasonModal, { type LostReasonResult } from './LostReasonModal';
import { AddStateColumn } from '../kanban/AddStateColumn';
import type { Lead, LeadLostReason, LeadStage, StageTotals } from '../../services/api/leads';
import { useReorderLead } from '../../hooks/useLeads';
import { groupLeadsByStage, isClosedLead } from './leadStages';
import { leadActivityAt } from './leadActivity';

interface LeadKanbanBoardProps {
  leads: Lead[];
  // MKS-345 — the configured pipeline, in board order. Was the frozen
  // `LEAD_STAGES` tuple this module used to import.
  stages: LeadStage[];
  stageTotals: StageTotals;
  staleDays: number;
  dealValueCZKMap: Map<number, number>;
  onCardClick: (lead: Lead) => void;
  // Phase 105 Plan 105-08 — see the docblock below. Defaults to 'internal'
  // so every existing call site (the shipped `sales/LeadsPage.tsx` board)
  // is unchanged.
  audience?: 'internal' | 'external';
  /**
   * The internal board (`sales/LeadsPage.tsx`) passes this; the
   * external-portal board (`pages/external/LeadsPage.tsx`) omits it. When
   * true, column headers carry the weighted total, and — when the stage
   * handlers below are given too — rename / recolour / delete in place with
   * a trailing "+ Add stage" cell, the same interactions `AddStateColumn`
   * and `StateListEditor` give the ticket board.
   */
  configurable?: boolean;
  onAddStage?: (name: string, color: string) => void;
  onRenameStage?: (stageId: number, name: string) => void;
  onRecolorStage?: (stageId: number, color: string) => void;
  onDeleteStage?: (stageId: number) => void;
  /**
   * MKS-347 — an INTERNAL viewer who may not write: the marketing role, whose
   * API access to leads is read-only (`requireSalesOrMarketing` on the reads,
   * `requireSales` on every write). Same no-drag outcome as `audience:
   * 'external'` but a distinct prop, because external also strips the
   * internal-only affordances on the card and this must not.
   */
  readOnly?: boolean;
  /**
   * MKS-347 — the configurable lost-reason list. Passed in rather than
   * fetched here so the external board (which has no internal session) never
   * fires the request; its absence simply means no modal, and external cannot
   * drag anyway.
   */
  lostReasons?: LeadLostReason[];
}

/**
 * Same component for both surfaces; `audience` prop drives the only
 * branching (no conditional auth-hook imports — audience is the seam),
 * mirroring `frontend/src/pages/projects/KanbanBoardPage.tsx:106-107`.
 *
 * This must never be replaced by an internal auth-hook import — this
 * component must not import the internal auth-context module or its hook
 * (v4.0 Landmine #8). The external Leads page passes `audience="external"`
 * explicitly instead.
 *
 * Phase 105 Plan 105-08.
 */
export default function LeadKanbanBoard({
  leads,
  stages,
  stageTotals,
  staleDays,
  dealValueCZKMap,
  onCardClick,
  audience = 'internal',
  configurable = false,
  onAddStage,
  onRenameStage,
  onRecolorStage,
  onDeleteStage,
  readOnly = false,
  lostReasons,
}: LeadKanbanBoardProps) {
  const isExternal = audience === 'external';
  // MKS-347 — the two no-write audiences, collapsed into the one question
  // every handler below actually asks.
  const canDrag = !isExternal && !readOnly;
  const [activeId, setActiveId] = useState<number | null>(null);
  // MKS-347 — a drag that would move a lead into a lost column is HELD here
  // until a reason is given; `null` means no such drag is pending.
  const [pendingLostMove, setPendingLostMove] = useState<{
    lead: Lead;
    stageId: number;
    beforeLeadId?: number;
    afterLeadId?: number;
  } | null>(null);
  // T-108-HOOKRULES: called unconditionally regardless of audience; the
  // mutation is simply never invoked for external (see handleDragEnd).
  const reorderLeadMutation = useReorderLead();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  // External never starts a drag: an empty sensor list means dnd-kit's
  // PointerSensor is never registered, so no pointer interaction can
  // activate a drag, while DndContext itself stays mounted (LeadColumn's
  // useDroppable / LeadCard's useSortable need the context ancestor).
  // MKS-347 — a read-only internal viewer (marketing) is in exactly the same
  // position, so it takes the same empty sensor list.
  const activeSensors = canDrag ? sensors : [];

  const leadsByStage = useMemo(() => groupLeadsByStage(leads, stages), [leads, stages]);

  const staleLeadIds = useMemo(() => {
    if (staleDays <= 0) return new Set<number>();
    const threshold = new Date();
    threshold.setDate(threshold.getDate() - staleDays);
    const ids = new Set<number>();
    for (const lead of leads) {
      // MKS-345 — staleness still skips closed deals, but on the stage's
      // KIND, so a renamed or newly added closing column behaves the same.
      if (isClosedLead(lead)) continue;
      // MKS-294: one set feeds both the per-card marker (via LeadColumn) and
      // the "N stale leads" badge below, so the count can never disagree with
      // the cards it is counting.
      if (new Date(leadActivityAt(lead)) < threshold) {
        ids.add(lead.id);
      }
    }
    return ids;
  }, [leads, staleDays]);

  const staleCount = staleLeadIds.size;

  const activeLead = activeId ? leads.find((l) => l.id === activeId) ?? null : null;

  // The add/edit affordances appear only for an internal actor whose page
  // handed down the handlers (admin or sales).
  // MKS-347 — and never for a read-only internal viewer.
  const canManageStages = configurable && canDrag && Boolean(onAddStage);

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as number);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    // Belt-and-braces second check (T-108-WRITE): even though neither the
    // external audience nor a read-only internal viewer registers a sensor,
    // never invoke the mutation for either.
    if (!canDrag) return;

    const { active, over } = event;
    if (!over) return;

    const leadId = active.id as number;
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) return;

    // `over.id` is either a column id (MKS-345: the stage's id) when dropping
    // on empty column space, or a lead id when dropping directly onto another
    // card. In the latter case, resolve the stage from the target card's lead.
    // Both are numbers now, so the lead lookup is what tells them apart.
    const overLead = leads.find((l) => l.id === over.id);
    const targetStage = overLead ? overLead.stageId : (over.id as number);

    // MKS-213: compute the resulting order of the TARGET column, derive
    // exactly ONE anchor from it, and let the server renumber the whole real
    // column around it. Accepted consequence of a filtered board: with a
    // filter active, the anchor is the neighbour the user can SEE, so the
    // card lands next to the card it was dropped on — the intuitive result —
    // and the server renumbers the whole real column.
    const targetColumn = leadsByStage[targetStage] ?? [];
    const sameColumn = lead.stageId === targetStage;

    let resultColumn: Lead[];
    if (sameColumn) {
      const fromIndex = targetColumn.findIndex((l) => l.id === leadId);
      const toIndex = overLead
        ? targetColumn.findIndex((l) => l.id === overLead.id)
        : targetColumn.length - 1;
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;
      resultColumn = arrayMove(targetColumn, fromIndex, toIndex);
    } else {
      const withoutDragged = targetColumn.filter((l) => l.id !== leadId);
      const dropIndex = overLead
        ? withoutDragged.findIndex((l) => l.id === overLead.id)
        : withoutDragged.length;
      const insertAt = dropIndex === -1 ? withoutDragged.length : dropIndex;
      resultColumn = [
        ...withoutDragged.slice(0, insertAt),
        lead,
        ...withoutDragged.slice(insertAt),
      ];
    }

    const draggedIndex = resultColumn.findIndex((l) => l.id === leadId);
    const anchor: { beforeLeadId?: number; afterLeadId?: number } = {};
    if (draggedIndex > 0) {
      anchor.afterLeadId = resultColumn[draggedIndex - 1]!.id;
    } else if (resultColumn.length > 1) {
      anchor.beforeLeadId = resultColumn[draggedIndex + 1]!.id;
    }

    // MKS-347 — dropping a deal into the lost column asks why first. The move
    // is HELD, not applied-then-amended: the backend rejects a lost move with
    // no reason, so firing the mutation now would just be a 400 plus a
    // rolled-back optimistic update. Only a move that ENTERS the lost column
    // asks — reordering inside it, or leaving it, does not.
    const enteringLost =
      !sameColumn &&
      lead.stageKind !== 'lost' &&
      stages.find((s) => s.id === targetStage)?.kind === 'lost';
    if (enteringLost && lostReasons) {
      setPendingLostMove({ lead, stageId: targetStage, ...anchor });
      return;
    }

    reorderLeadMutation.mutate({ id: leadId, stageId: targetStage, ...anchor });
  }

  function confirmLostMove(result: LostReasonResult) {
    if (!pendingLostMove) return;
    const { lead, ...move } = pendingLostMove;
    reorderLeadMutation.mutate({ id: lead.id, ...move, ...result });
    setPendingLostMove(null);
  }

  return (
    <div>
      {staleCount > 0 && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-sm text-red-700">
          <span className="inline-flex h-2 w-2 rounded-full bg-red-500" />
          {staleCount} stale {staleCount === 1 ? 'lead' : 'leads'}
        </div>
      )}
      <DndContext
        sensors={activeSensors}
        onDragStart={canDrag ? handleDragStart : undefined}
        onDragEnd={canDrag ? handleDragEnd : undefined}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((s) => (
            <LeadColumn
              key={s.id}
              stage={s}
              leads={leadsByStage[s.id] ?? []}
              totalValue={stageTotals[String(s.id)]?.value ?? {}}
              staleLeadIds={staleLeadIds}
              dealValueCZKMap={dealValueCZKMap}
              onCardClick={onCardClick}
              audience={audience}
              readOnly={readOnly}
              configurable={configurable}
              onRename={onRenameStage}
              onRecolor={onRecolorStage}
              onDelete={onDeleteStage}
            />
          ))}
          {/* MKS-345 — the ticket board's own trailing "+ Add state" cell,
              reused verbatim: same component, same collapsed-button →
              name-input interaction, same deterministic palette step. */}
          {canManageStages && (
            <div className="w-56 shrink-0 self-start">
              <AddStateColumn stateCount={stages.length} onAdd={onAddStage!} />
            </div>
          )}
        </div>
        {canDrag && (
          <DragOverlay>
            {activeLead && (
              <LeadCard
                lead={activeLead}
                isStale={staleLeadIds.has(activeLead.id)}
                dealValueCZK={dealValueCZKMap.get(activeLead.id)}
                onClick={() => {}}
                audience={audience}
              />
            )}
          </DragOverlay>
        )}
      </DndContext>

      {/* MKS-347 — the held drag from handleDragEnd. Cancelling drops the
          move entirely: nothing was mutated, so the card simply stays where
          it was. */}
      {pendingLostMove && (
        <LostReasonModal
          isOpen
          leadName={pendingLostMove.lead.name}
          reasons={lostReasons ?? []}
          isSaving={reorderLeadMutation.isPending}
          onCancel={() => setPendingLostMove(null)}
          onConfirm={confirmLostMove}
        />
      )}
    </div>
  );
}
